import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Alert, Badge, Card, CardBody, Col, Input, Row, Spinner } from 'reactstrap';
import MedDataTable from '../common/MedDataTable';
import TablePageSizeSelect from '../common/TablePageSizeSelect';
import AnalysisSegmentCharts from './AnalysisSegmentCharts';
import useStoredTablePageSize from '../../hooks/useStoredTablePageSize';
import apiBase from '../../apiConfig';
import { formatSelectedPeriod } from '../../helpers/testPeriod';
import { getAuthHeaders } from '../../helpers/utils';

const FACULTIES_ENDPOINT = `${apiBase}/p-admin/bezovtalanish.php?action=faculties`;
const GROUP_ENDPOINT = `${apiBase}/p-admin/bezovtalanish.php?action=group`;

const readGroupId = search => {
  const params = new URLSearchParams(search || '');
  return (params.get('guruh_id') || '').trim();
};

const getFacultyKey = faculty => faculty.code || faculty.name;

const resultBadgeClass = tone => `psi-anxiety-result psi-anxiety-result--${tone || 'secondary'}`;

const AnxietyAnalysis = ({ history, location }) => {
  const [faculties, setFaculties] = useState([]);
  const [segments, setSegments] = useState({ courses: [], nationalities: [] });
  const [groupData, setGroupData] = useState(null);
  const [loadingFaculties, setLoadingFaculties] = useState(true);
  const [loadingGroup, setLoadingGroup] = useState(false);
  const [banner, setBanner] = useState(null);
  const [notFoundMessage, setNotFoundMessage] = useState('');
  const [activePeriodLabel, setActivePeriodLabel] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [expandedFaculties, setExpandedFaculties] = useState({});
  const [pageSize, setPageSize] = useStoredTablePageSize();

  const groupId = readGroupId(location.search);

  const requestConfig = useMemo(
    () => ({
      headers: getAuthHeaders()
    }),
    []
  );

  const changeGroup = groupName => {
    const search = groupName ? `?guruh_id=${encodeURIComponent(groupName)}` : '';
    history.push({
      pathname: '/bezovtalanish',
      search
    });
  };

  useEffect(() => {
    let active = true;

    const fetchFaculties = async () => {
      setLoadingFaculties(true);
      setBanner(null);

      try {
        const response = await axios.get(FACULTIES_ENDPOINT, requestConfig);

        if (!active) {
          return;
        }

        if (response.data && response.data.success) {
          setFaculties(response.data.faculties || []);
          setSegments(response.data.segments || { courses: [], nationalities: [] });
          setActivePeriodLabel(formatSelectedPeriod(response.data.period));
          return;
        }

        throw new Error((response.data && response.data.message) || 'Bezovtalanish ma`lumotlari yuklanmadi.');
      } catch (error) {
        if (!active) {
          return;
        }

        const message =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          'Bezovtalanish ma`lumotlarini yuklashda xatolik yuz berdi.';

        setBanner({ color: 'danger', text: message });
      } finally {
        if (active) {
          setLoadingFaculties(false);
        }
      }
    };

    fetchFaculties();

    return () => {
      active = false;
    };
  }, [requestConfig]);

  useEffect(() => {
    let active = true;

    if (!groupId) {
      setGroupData(null);
      setLoadingGroup(false);
      setNotFoundMessage('');
      return undefined;
    }

    const fetchGroup = async () => {
      setLoadingGroup(true);
      setBanner(null);
      setNotFoundMessage('');

      try {
        const response = await axios.get(GROUP_ENDPOINT, {
          ...requestConfig,
          params: {
            guruh_id: groupId
          }
        });

        if (!active) {
          return;
        }

        if (response.data && response.data.success) {
          setGroupData(response.data.data || null);
          setActivePeriodLabel(formatSelectedPeriod(response.data.period));
          return;
        }

        throw new Error((response.data && response.data.message) || 'Guruh ma`lumotlari yuklanmadi.');
      } catch (error) {
        if (!active) {
          return;
        }

        const status = error.response && error.response.status;
        const message =
          (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          'Guruh ma`lumotlarini yuklashda xatolik yuz berdi.';

        setGroupData(null);

        if (status === 404) {
          setNotFoundMessage(message);
          return;
        }

        setBanner({ color: 'danger', text: message });
      } finally {
        if (active) {
          setLoadingGroup(false);
        }
      }
    };

    fetchGroup();

    return () => {
      active = false;
    };
  }, [groupId, requestConfig]);

  const filteredFaculties = useMemo(() => {
    const search = groupSearch.trim().toLowerCase();

    return faculties
      .map(faculty => {
        const matchesFaculty = faculty.name.toLowerCase().includes(search);
        const groups = (faculty.groups || []).filter(group => {
          if (!search || matchesFaculty) {
            return true;
          }

          return group.name.toLowerCase().includes(search);
        });

        return {
          ...faculty,
          groups
        };
      })
      .filter(faculty => faculty.groups.length > 0 || faculty.name.toLowerCase().includes(groupSearch.trim().toLowerCase()));
  }, [faculties, groupSearch]);

  useEffect(() => {
    if (!filteredFaculties.length) {
      return;
    }

    setExpandedFaculties(prev => {
      const next = { ...prev };

      if (groupId) {
        const selectedFaculty = filteredFaculties.find(faculty =>
          (faculty.groups || []).some(group => group.name === groupId)
        );

        if (selectedFaculty) {
          next[getFacultyKey(selectedFaculty)] = true;
          return next;
        }
      }

      if (!Object.values(next).some(Boolean)) {
        next[getFacultyKey(filteredFaculties[0])] = true;
      }

      return next;
    });
  }, [filteredFaculties, groupId]);

  const toggleFaculty = facultyKey => {
    setExpandedFaculties(prev => ({
      ...prev,
      [facultyKey]: !prev[facultyKey]
    }));
  };

  const overviewStats = useMemo(() => {
    const statsMap = {};

    faculties.forEach(faculty => {
      ((faculty.stats && faculty.stats.items) || []).forEach(item => {
        if (!statsMap[item.natija]) {
          statsMap[item.natija] = {
            ...item,
            count: 0
          };
        }

        statsMap[item.natija].count += Number(item.count || 0);
      });
    });

    const items = Object.values(statsMap).sort((left, right) => left.natija - right.natija);
    const total = items.reduce((sum, item) => sum + Number(item.count || 0), 0);

    return {
      total,
      items: items.map(item => ({
        ...item,
        percent: total > 0 ? Number(((item.count / total) * 100).toFixed(1)) : 0
      }))
    };
  }, [faculties]);

  const filteredStudents = useMemo(() => {
    const students = (groupData && groupData.students) || [];
    const search = studentSearch.trim().toLowerCase();

    if (!search) {
      return students;
    }

    return students.filter(
      student =>
        student.full_name.toLowerCase().includes(search) ||
        student.group.toLowerCase().includes(search) ||
        student.result.toLowerCase().includes(search) ||
        String(student.talaba_id).includes(search)
    );
  }, [groupData, studentSearch]);

  const columns = useMemo(
    () => [
      {
        dataField: 'order',
        text: 'T/R',
        sort: true,
        classes: 'align-middle font-weight-semi-bold'
      },
      {
        dataField: 'full_name',
        text: 'F.I.O',
        sort: true,
        classes: 'align-middle',
        formatter: (value, row) => (
          <div className="psi-anxiety-student">
            <div className="psi-anxiety-student__avatar">{row.initials}</div>
            <div className="psi-anxiety-student__copy">
              <strong>{value}</strong>
              <span>Talaba ID: {row.talaba_id}</span>
            </div>
          </div>
        )
      },
      {
        dataField: 'group',
        text: 'Guruh',
        sort: true,
        classes: 'align-middle',
        formatter: value => <span className="psi-anxiety-group-pill">{value}</span>
      },
      {
        dataField: 'result',
        text: 'Xususiyati',
        sort: true,
        classes: 'align-middle',
        formatter: (value, row) => (
          <Badge pill className={resultBadgeClass(row.tone)}>
            {value}
          </Badge>
        )
      }
    ],
    []
  );

  return (
    <>
      <div className="psi-anxiety-heading mb-3">
        <div>
          <div className="psi-anxiety-heading__nav">Tahlillar / Bezovtalanish</div>
          <h3 className="mb-1">Bezovtalanish tahlili</h3>
          <p className="mb-0">Fakultet va guruhlar kesimida bezovtalanish darajasini kuzating.</p>
        </div>
        {activePeriodLabel || groupId ? (
          <div className="psi-heading-badges">
            {activePeriodLabel ? <Badge pill className="psi-active-period-badge">{activePeriodLabel}</Badge> : null}
            {groupId ? (
              <Badge pill className="psi-anxiety-heading__badge">
                {groupId}
              </Badge>
            ) : null}
          </div>
        ) : null}
      </div>

      {banner ? (
        <Alert color={banner.color} className="mb-3">
          {banner.text}
        </Alert>
      ) : null}

      <div className="psi-anxiety-toolbar mb-3">
        <div className="psi-anxiety-toolbar__copy">
          <h5 className="mb-1">{groupId ? `${groupId} guruhi` : 'Bezovtalanish ro‘yxati'}</h5>
          <p className="text-600 mb-0">
            {groupId
              ? 'Tanlangan guruh bo‘yicha bezovtalanish natijalari va talabalar ro‘yxati.'
              : 'Fakultetni ochib, kerakli guruhni tanlang.'}
          </p>
        </div>

        <div className="psi-anxiety-toolbar__controls">
          <Input
            value={groupSearch}
            onChange={({ target }) => setGroupSearch(target.value)}
            placeholder="Fakultet yoki guruh qidirish"
            className="psi-anxiety-search"
          />
        </div>
      </div>

      <AnalysisSegmentCharts segments={segments} />

      {notFoundMessage ? (
        <Alert color="warning" className="mb-3">
          {notFoundMessage}
        </Alert>
      ) : null}

      <Row className="mb-3 psi-anxiety-layout">
        <Col xl="5" className="mb-3 mb-xl-0">
          <Card className="border-0 shadow-sm psi-anxiety-sidebar-card">
            <CardBody className="psi-anxiety-sidebar-card__body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="mb-0">Fakultetlar va guruhlar</h5>
                {loadingFaculties ? <Spinner size="sm" color="primary" /> : <Badge color="light">{filteredFaculties.length} ta</Badge>}
              </div>

              <div className="psi-anxiety-faculty-list">
                {loadingFaculties ? (
                  <div className="text-center text-500 py-4">Bezovtalanish ma`lumotlari yuklanmoqda...</div>
                ) : filteredFaculties.length > 0 ? (
                  filteredFaculties.map(faculty => {
                    const facultyKey = getFacultyKey(faculty);
                    const isOpen = !!expandedFaculties[facultyKey];

                    return (
                      <div key={facultyKey} className="psi-anxiety-faculty">
                        <button
                          type="button"
                          className={`psi-anxiety-faculty__header ${isOpen ? 'is-open' : ''}`}
                          onClick={() => toggleFaculty(facultyKey)}
                        >
                          <div className="psi-anxiety-faculty__copy">
                            <strong>{faculty.name}</strong>
                            <span>
                              {faculty.group_count || (faculty.groups || []).length} ta guruh • {faculty.student_count} talaba
                            </span>
                          </div>
                          <span className="psi-anxiety-faculty__toggle" aria-hidden="true">
                            {isOpen ? '−' : '+'}
                          </span>
                        </button>

                        {isOpen ? (
                          <div className="psi-anxiety-faculty__body">
                            <div className="psi-anxiety-mini-stats">
                              {((faculty.stats && faculty.stats.items) || []).map(item => (
                                <div key={`${facultyKey}-${item.natija}`} className={`psi-anxiety-mini-stat psi-anxiety-mini-stat--${item.tone}`}>
                                  <div className="psi-anxiety-mini-stat__mark">{item.natija}</div>
                                  <div className="psi-anxiety-mini-stat__copy">
                                    <strong>{item.count}</strong>
                                    <span>{item.label}</span>
                                  </div>
                                  <small>{item.percent}%</small>
                                </div>
                              ))}
                            </div>

                            <div className="psi-anxiety-group-grid">
                              {(faculty.groups || []).map(group => (
                                <button
                                  key={group.name}
                                  type="button"
                                  className={`psi-anxiety-group-button ${groupId === group.name ? 'is-active' : ''}`}
                                  onClick={() => changeGroup(group.name)}
                                >
                                  <span>{group.name}</span>
                                  <small>{Number(group.result_count || 0)}/{Number(group.student_count || 0)}</small>
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center text-500 py-4">Mos fakultet yoki guruh topilmadi.</div>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col xl="7" className="psi-anxiety-main-col">
          {loadingGroup ? (
            <Card className="border-0 shadow-sm h-100">
              <CardBody className="d-flex align-items-center justify-content-center py-6">
                <div className="text-center">
                  <Spinner color="primary" className="mb-3" />
                  <div className="text-600">Guruh natijalari yuklanmoqda...</div>
                </div>
              </CardBody>
            </Card>
          ) : groupData ? (
            <div className="psi-anxiety-main-shell">
              <Row className="mb-3">
                {(groupData.stats.items || []).map(item => (
                  <Col md="6" xl="3" key={`${groupData.group}-${item.natija}`} className="mb-3">
                    <Card className={`border-0 shadow-sm h-100 psi-anxiety-stat psi-anxiety-stat--${item.tone}`}>
                      <CardBody>
                        <div className="psi-anxiety-stat__mark">{item.natija}</div>
                        <div className="psi-anxiety-stat__value">{item.count}</div>
                        <div className="psi-anxiety-stat__label">{item.label}</div>
                        <div className="psi-anxiety-stat__meta">{item.percent}%</div>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>

              <Card className="border-0 shadow-sm psi-anxiety-main-card">
                <CardBody className="psi-anxiety-main-card__body">
                  <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-3">
                    <div className="mb-3 mb-lg-0">
                      <h5 className="mb-1">{groupData.group} guruhi kesimida</h5>
                      <p className="text-600 mb-0">
                        {groupData.faculty} • {groupData.student_count} ta talaba
                      </p>
                    </div>
                    <div className="d-flex flex-column flex-sm-row">
                      <Input
                        value={studentSearch}
                        onChange={({ target }) => setStudentSearch(target.value)}
                        placeholder="Talaba qidirish"
                        className="psi-anxiety-search"
                      />
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                    <Badge color="light" pill className="px-3 py-2">
                      {filteredStudents.length} ta yozuv
                    </Badge>
                    <TablePageSizeSelect value={pageSize} onChange={setPageSize} className="med-table-page-size mt-2 mt-sm-0" />
                  </div>

                  <div className="psi-anxiety-table-scroll">
                    <MedDataTable
                      data={filteredStudents}
                      columns={columns}
                      keyField="id"
                      pageSize={pageSize}
                      paginationResetKey={`${groupData.group}-${studentSearch}`}
                      noDataIndication={<div className="text-center py-4 text-500">Bu guruh bo‘yicha talaba topilmadi.</div>}
                      tableClasses="table table-hover fs--1 mb-0 med-dashboard-table psi-anxiety-table"
                      headerClasses="bg-200 text-900"
                      wrapperClasses="table-responsive psi-anxiety-table-wrap"
                    />
                  </div>
                </CardBody>
              </Card>
            </div>
          ) : (
            <Card className="border-0 shadow-sm h-100 psi-anxiety-overview-card">
              <CardBody className="psi-anxiety-overview-card__body">
                <div className="psi-anxiety-overview-card__hero">
                  <div>
                    <div className="psi-anxiety-overview-card__eyebrow">Bezovtalanish taqsimot</div>
                    <h4 className="mb-1">Fakultetdan guruh tanlang</h4>
                    <p className="text-600 mb-0">Tanlangan guruhning bezovtalanish statistikasi va talabalar ro‘yxati shu yerda ko‘rinadi.</p>
                  </div>
                  <Badge color="light" pill className="px-3 py-2">
                    {overviewStats.total} natija
                  </Badge>
                </div>

                <Row className="mt-3">
                  {overviewStats.items.map(item => (
                    <Col md="6" key={`overview-${item.natija}`} className="mb-3">
                      <div className={`psi-anxiety-overview-stat psi-anxiety-overview-stat--${item.tone}`}>
                        <div className="psi-anxiety-overview-stat__mark">{item.natija}</div>
                        <div className="psi-anxiety-overview-stat__copy">
                          <strong>{item.label}</strong>
                          <span>{item.count} ta natija</span>
                        </div>
                        <small>{item.percent}%</small>
                      </div>
                    </Col>
                  ))}
                </Row>
              </CardBody>
            </Card>
          )}
        </Col>
      </Row>
    </>
  );
};

export default AnxietyAnalysis;
